import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t, type IntlService } from 'ember-intl';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import { PhArrowLeft, PhArrowRight, PhSparkle, PhInfo } from 'ember-phosphor-icons';
import type RouterService from '@ember/routing/router-service';
import { reloadRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  buildTasTransitionFieldData,
  requestCoursesApprovalRequest,
  requestEvidenceApprovalRequest,
} from 'tio-data/requests/tas-application-actions';
import { buildTasEmployeeActor } from 'tio-data/utils/tas-actor';
import WizardStepIndicator from './wizard-step-indicator';
import type { WizardStep } from './wizard-step-indicator';
import PhaseExplainer from './phase-explainer';
import CourseEditor from './course-editor';
import DocumentRequirements from './document-requirements';
import SubmissionGuard from './submission-guard';
import AlertBanner from 'tio-ui/components/alert-banner';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';

type ApplicationType = 'type-two' | 'type-three' | 'type-four';

interface ApplicationWizardSignature {
  Args: {
    /** The TAS application being created/edited */
    application: TasApplicationModel;
    /** The application type (type-two, type-three, type-four) */
    applicationType: ApplicationType;
    /** The program template controlling form configuration */
    programTemplate: TasProgramTemplateModel;
  };
  Element: HTMLDivElement;
}

/**
 * ApplicationWizard - Multi-step guided TAS application wizard
 *
 * Replaces the legacy monolithic form with a step-by-step guided experience.
 * Steps vary by program type. Wraps existing upload infrastructure with
 * better UX and hard guards against empty submissions.
 *
 * @example
 * ```gts
 * <ApplicationWizard
 *   @application={{this.application}}
 *   @applicationType="type-two"
 *   @programTemplate={{this.programTemplate}}
 * />
 * ```
 */
export default class ApplicationWizard extends Component<ApplicationWizardSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked currentStep = 0;
  @tracked isSubmitting = false;

  get programName() {
    return this.args.application.tasProgramInstance?.tasProgramTemplate?.programName ?? '';
  }

  get isTypeTwo() {
    return this.args.applicationType === 'type-two';
  }

  get isTypeThree() {
    return this.args.applicationType === 'type-three';
  }

  get isTypeFour() {
    return this.args.applicationType === 'type-four';
  }

  get attachmentClassification() {
    return this.isTypeThree ? 'COURSE_APPROVAL_DOCUMENTS' : 'COMPLETION_DOCUMENTS';
  }

  /**
   * Whether the program template has configured AI-classifiable document types
   * for the current attachment step. When true, render the categorized-upload
   * component (real-time AI feedback). When false, fall back to the legacy
   * free-form upload component so unconfigured programs are unaffected.
   */
  get useCategorizedUpload() {
    return (
      this.args.programTemplate.requiredDocumentTypesFor(this.attachmentClassification).length > 0
    );
  }

  get stepLabels(): string[] {
    const base = [
      this.intl.t('tuition_assistance.reimagined.wizard.step.overview'),
      this.intl.t('tuition_assistance.reimagined.wizard.step.courses'),
      this.isTypeThree
        ? this.intl.t('tuition_assistance.reimagined.wizard.step.pre_approval_docs')
        : this.intl.t('tuition_assistance.reimagined.wizard.step.evidence_docs'),
      this.intl.t('tuition_assistance.reimagined.wizard.step.review'),
    ];
    return base;
  }

  get steps(): WizardStep[] {
    return this.stepLabels.map((label, index) => ({
      number: index + 1,
      label,
      completed: index < this.currentStep,
      active: index === this.currentStep,
    }));
  }

  get totalSteps() {
    return this.stepLabels.length;
  }

  get isFirstStep() {
    return this.currentStep === 0;
  }

  get isLastStep() {
    return this.currentStep === this.totalSteps - 1;
  }

  get canAdvance() {
    if (this.currentStep === 1) {
      return (this.args.application.activeCourses?.length ?? 0) > 0;
    }
    return true;
  }

  get hasRequiredAttachments() {
    if (this.isTypeThree) {
      return this.args.application.hasRequiredPreApprovalAttachments ?? true;
    }
    return this.args.application.hasRequiredEvidenceAttachments ?? true;
  }

  get hasCourses() {
    return (this.args.application.activeCourses?.length ?? 0) > 0;
  }

  get submitLabel() {
    return this.isTypeThree
      ? this.intl.t('tuition_assistance.reimagined.wizard.submit.pre_approval')
      : this.intl.t('tuition_assistance.reimagined.wizard.submit.evidence');
  }

  formatCurrency = (value: number) =>
    this.intl.formatNumber(value / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });

  nextStep = () => {
    if (this.currentStep < this.totalSteps - 1 && this.canAdvance) {
      this.currentStep++;
    }
  };

  previousStep = () => {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  };

  submitApplication = async () => {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    try {
      // WarpDrive mutation builders (Phase 2.7) — replace
      // adapter.requestCoursesApproval(application) / adapter.requestCourseEvidence(application)
      // (triggerActionAndReload → request-courses-approval / request-evidence-approval transition + reload).
      const application = this.args.application;
      const actor = buildTasEmployeeActor(
        this.sessionContext.user,
        this.sessionContext.currentRole
      );
      const fieldData = buildTasTransitionFieldData(application.fields);
      if (this.isTypeThree) {
        await this.store.request(
          requestCoursesApprovalRequest(application.id as string, actor, '', fieldData)
        );
      } else {
        await this.store.request(
          requestEvidenceApprovalRequest(application.id as string, actor, '', fieldData)
        );
      }
      // TAS state transitions go through StateTransitionableController#transition
      // → `head 204` (renders NO resource), so the builder does NOT refresh the
      // cache — keep the reload via the helper.
      await reloadRecord(this.store, application);
      this.router.transitionTo('authenticated.reimagined.tas.dashboard');
    } catch {
      this.isSubmitting = false;
    }
  };

  exitWizard = () => {
    this.router.transitionTo('authenticated.reimagined.tas.dashboard');
  };

  <template>
    <VStack class="gap-6" ...attributes>
      {{! Step indicator }}
      <WizardStepIndicator @steps={{this.steps}} />

      {{! Step content }}
      {{#if (this.isStep 0)}}
        {{! STEP 1: Overview }}
        <div class="rounded-3xl tio-gradient-brand p-8 tio-fg-inverse shadow-xl">
          <div class="tio-row-tight tio-text-sm-semibold mb-2">
            <PhSparkle class="tio-icon-sm" />
            <span>{{t "tuition_assistance.reimagined.wizard.overview.badge"}}</span>
          </div>
          <Heading @level={{1}} class="tio-fg-inverse">
            {{t "tuition_assistance.reimagined.wizard.overview.title"}}
          </Heading>
          <Text class="tio-tas-wizard-hero-copy mt-2">
            {{this.programName}}
          </Text>
        </div>

        <PhaseExplainer @state={{@application.state}} />

        {{#if this.isTypeThree}}
          <AlertBanner @intent="info">
            {{t "tuition_assistance.reimagined.wizard.overview.pre_approval_explainer"}}
          </AlertBanner>
        {{else}}
          <AlertBanner @intent="info">
            {{t "tuition_assistance.reimagined.wizard.overview.evidence_explainer"}}
          </AlertBanner>
        {{/if}}

        <Section>
          <:header>
            <div class="tio-row-tight">
              <PhInfo @size={{20}} class="tio-fg-info" />
              <Heading @level={{3}}>
                {{t "tuition_assistance.reimagined.wizard.overview.what_to_expect"}}
              </Heading>
            </div>
          </:header>
          <:body>
            <ol class="list-decimal list-inside space-y-2 text-sm tio-fg-secondary">
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_1"}}</li>
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_2"}}</li>
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_3"}}</li>
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_4"}}</li>
            </ol>
          </:body>
        </Section>
      {{/if}}

      {{#if (this.isStep 1)}}
        {{! STEP 2: Courses }}
        <Section>
          <:header>
            <Heading @level={{2}}>
              {{t "tuition_assistance.reimagined.wizard.courses.title"}}
            </Heading>
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.courses.subtitle"}}
            </Text>
          </:header>
          <:body>
            <CourseEditor @application={{@application}} @programTemplate={{@programTemplate}} />
          </:body>
        </Section>

        {{#unless this.hasCourses}}
          <AlertBanner @intent="warning">
            {{t "tuition_assistance.reimagined.wizard.courses.required_message"}}
          </AlertBanner>
        {{/unless}}
      {{/if}}

      {{#if (this.isStep 2)}}
        {{! STEP 3: Documents }}
        <Section>
          <:header>
            <Heading @level={{2}}>
              {{#if this.isTypeThree}}
                {{t "tuition_assistance.reimagined.wizard.documents.pre_approval_title"}}
              {{else}}
                {{t "tuition_assistance.reimagined.wizard.documents.evidence_title"}}
              {{/if}}
            </Heading>
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.documents.subtitle"}}
            </Text>
          </:header>
          <:body>
            <DocumentRequirements
              @programTemplate={{@programTemplate}}
              @classification={{this.attachmentClassification}}
              @uploadedCount={{this.uploadedCount}}
            />

            <div class="mt-6">
              {{#if this.useCategorizedUpload}}
                <CategorizedUpload
                  @programTemplate={{@programTemplate}}
                  @classification={{this.attachmentClassification}}
                  @attachable={{@application}}
                  @attachableType="tasApplication"
                  @existingAssets={{@application.tasAssets}}
                />
              {{else}}
                <UploadAttachments
                  @programTemplate={{@programTemplate}}
                  @classification={{this.attachmentClassification}}
                  @attachable={{@application}}
                  @attachableType="tasApplication"
                  @useDropzone={{true}}
                  @uploadRequired={{true}}
                  @hideDiscarded={{true}}
                />
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if (this.isStep 3)}}
        {{! STEP 4: Review & Submit }}
        <Section>
          <:header>
            <Heading @level={{2}}>
              {{t "tuition_assistance.reimagined.wizard.review.title"}}
            </Heading>
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.review.subtitle"}}
            </Text>
          </:header>
          <:body>
            <div class="space-y-4">
              {{! Course summary }}
              <div class="rounded-xl border tio-border-default tio-bg-surface p-5">
                <Text @weight="semibold" class="mb-2">
                  {{t "tuition_assistance.reimagined.wizard.review.courses_summary"}}
                </Text>
                {{#each @application.activeCourses as |course|}}
                  <div class="tio-row-between py-2 border-b tio-border-subtle last:border-0">
                    <Text @size="sm">{{course.displayName}}</Text>
                    <Text @size="sm" @weight="medium">
                      {{this.formatCurrency course.courseTotal}}
                    </Text>
                  </div>
                {{/each}}
              </div>

              {{! Document summary }}
              <div class="rounded-xl border tio-border-default tio-bg-surface p-5">
                <Text @weight="semibold" class="mb-2">
                  {{t "tuition_assistance.reimagined.wizard.review.documents_summary"}}
                </Text>
                <Text @variant="muted" @size="sm">
                  {{t
                    "tuition_assistance.reimagined.wizard.review.documents_count"
                    count=this.uploadedCount
                  }}
                </Text>
              </div>

              {{! Submission guard }}
              <SubmissionGuard
                @hasRequiredAttachments={{this.hasRequiredAttachments}}
                @hasCourses={{this.hasCourses}}
                @onSubmit={{this.submitApplication}}
                @isSubmitting={{this.isSubmitting}}
                @submitLabel={{this.submitLabel}}
              />
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Navigation buttons }}
      <div class="tio-row-between pt-4 border-t tio-border-default">
        {{#if this.isFirstStep}}
          <Button @appearance="outlined" {{on "click" this.exitWizard}}>
            <PhArrowLeft @size={{16}} class="mr-1" />
            {{t "tuition_assistance.reimagined.wizard.nav.back_to_dashboard"}}
          </Button>
        {{else}}
          <Button @appearance="outlined" {{on "click" this.previousStep}}>
            <PhArrowLeft @size={{16}} class="mr-1" />
            {{t "tuition_assistance.reimagined.wizard.nav.previous"}}
          </Button>
        {{/if}}

        {{#unless this.isLastStep}}
          <Button
            @intent="primary"
            disabled={{if this.canAdvance false true}}
            {{on "click" this.nextStep}}
          >
            {{t "tuition_assistance.reimagined.wizard.nav.next"}}
            <PhArrowRight @size={{16}} class="ml-1" />
          </Button>
        {{/unless}}
      </div>
    </VStack>
  </template>

  isStep = (step: number) => {
    return this.currentStep === step;
  };

  get uploadedCount() {
    if (this.isTypeThree) {
      return this.args.application.applicationApprovalAssets?.length ?? 0;
    }
    return this.args.application.evidenceAssets?.length ?? 0;
  }
}
